import { useState, useEffect } from "react";

import {
  Text,
  TwSelector,
  Button,
  Chip,
  Icon,
  Dialog,
  HelperText,
} from "@virkesborsen/ui-component-library";
import { useSubscription } from "../../../context/SubscriptionCheckoutContext";

export default function PropertySelect() {
  const {
    userProperties,
    addSelectedProperty,
    selectedProperties,
    removeSelectedProperty,
    errorMessages,
    setErrorMessages,
  } = useSubscription();

  const [isSelectPropertyOpen, setIsSelectPropertyOpen] = useState(false);

  useEffect(() => {
    if (selectedProperties.length > 0) {
      setErrorMessages((prev) => ({ ...prev, properties: undefined }));
    }
  }, [selectedProperties]);

  const handleSelect = (option) => {
    const selectedProperty = userProperties.find(
      (prop) => prop.ref === option.value
    );
    if (!selectedProperties.some((p) => p.ref === selectedProperty.ref)) {
      addSelectedProperty(selectedProperty);
    } else {
      removeSelectedProperty(selectedProperty);
    }
  };

  const handleRemove = (property) => {
    removeSelectedProperty(property);
  };

  const totalForestArea = selectedProperties.reduce(
    (acc, curr) => acc + curr.forestArea,
    0
  );
  const hasSelectedProperties = selectedProperties.length > 0;

  const hasBadData = selectedProperties.some(
    (property) => property?.hasBadData
  );

  return (
    <>
      <div className="w-full flex flex-col gap-1-5">
        <div className="flex flex-col gap-2 mx-4 mb-4">
          <Text variant="text-base/leading-normal/medium">Välj fastighet</Text>
          <Text
            variant="text-sm/leading-normal/normal"
            className="text-[#737373]"
          >
            Lägg till en eller flera fastigheter
          </Text>
        </div>
        <div className="flex flex-col rounded-2xl bg-white-50 p-4 w-full">
          <div
            className={`flex flex-row gap-1 justify-between items-center ${
              hasSelectedProperties ? "border-b border-stone-200 pb-4 mb-4" : ""
            }`}
          >
            <div className="flex flex-col gap-1">
              <Text variant="text-sm/leading-normal/medium">
                {hasSelectedProperties
                  ? `Valda fastigheter (${selectedProperties.length})`
                  : "Välj fastigheter att aktivera plus på"}
              </Text>
              {hasSelectedProperties && (
                <Text
                  variant="text-sm/leading-normal/normal"
                  className="text-pebble-500"
                >
                  {totalForestArea} hektar skogsmark
                </Text>
              )}
            </div>
            <Button
              tw
              variant="secondary"
              size="small"
              onClick={() => setIsSelectPropertyOpen(true)}
            >
              {hasSelectedProperties ? "Ändra" : "Lägg till"}
            </Button>
          </div>
          <div className="flex flex-col gap-6">
            {selectedProperties.map((p) => (
              <div
                key={p.ref}
                className="flex flex-row gap-1 justify-between items-center"
              >
                <div className="flex flex-col gap-1">
                  <Text variant="text-sm/leading-normal/medium">
                    {p.designation}
                  </Text>
                  <Text
                    variant="text-sm/leading-normal/normal"
                    className="text-pebble-500"
                  >
                    {!p.forestArea ? "..." : `${p.forestArea} hektar skogsmark`}
                  </Text>
                </div>
                <Button
                  tw
                  variant="outline"
                  size="small"
                  onClick={() => handleRemove(p)}
                >
                  Ta bort
                </Button>
              </div>
            ))}
          </div>
          {hasBadData && (
            <div className="flex flex-row gap-2 bg-stone-100 rounded-md p-3 mt-4">
              <Icon
                name="InfoFilled"
                size="medium"
                className="fill-pebble-500"
              />
              <div className="flex flex-col gap-2">
                <Text
                  variant="text-xs/leading-normal/normal"
                  className="text-pebble-500"
                >
                  Tack för ditt val av fastighet! Vi ser att valda fastigheter
                  ligger i områden där den officella skogsdatan tyvärr är av{" "}
                  <b>generellt lägre kvalitet</b>. <br /> Men vi tror ändå att
                  Treebula Plus kan skapa stort värde för dig som skogsägare.
                  Som exempelvis jämförelsen för virkespriser och
                  marknadsinformation.
                </Text>
              </div>
            </div>
          )}
        </div>
        {errorMessages.properties && (
          <HelperText
            icon="ExclamationFilled"
            label={errorMessages.properties}
            className={"text-danger-500 fill-danger-500 pl-4"}
          />
        )}
      </div>

      {/* Select Property Dialog */}
      <Dialog isOpen={isSelectPropertyOpen} size="medium">
        <Dialog.Header>
          <div className="flex w-full justify-between">
            <Text variant="text-base/leading-normal/medium">
              Välj fastighet
            </Text>
            <Button
              tw
              variant="secondary"
              size="icon"
              onClick={() => setIsSelectPropertyOpen(false)}
            >
              <Icon
                tw
                name="CrossSimple"
                className="color-charcoal-500"
                size="medium"
              />
            </Button>
          </div>
        </Dialog.Header>
        <Dialog.Content>
          {!userProperties.length > 0 ? (
            <div className="flex flex-col gap-2">
              <Text
                variant="text-sm/leading-normal/semibold"
                className="text-danger-500"
              >
                Du har inga fastigheter på ditt konto.
              </Text>
              <Text variant="text-sm/leading-normal/normal">
                Stäng köp rutan och klicka på 'Karta' i menyn, sedan 'Lägg till
                fastighet' högst upp på sidan.
              </Text>
            </div>
          ) : (
            <TwSelector>
              {userProperties?.map((option) => (
                <div
                  key={option.ref}
                  className="flex flex-row gap-1 justify-between items-center relative"
                >
                  <TwSelector.Option
                    label={option.designation}
                    value={option.ref}
                    isSelected={selectedProperties?.some(
                      (p) => p.ref === option.ref
                    )}
                    onClick={handleSelect}
                    disabled={option.disabled || option.dfr}
                  />
                  {option.dfr && (
                    <Chip
                      label="Plus"
                      size="medium"
                      className="bg-[#F6F5FE] text-[#6856C3] fill-[#6856C3] absolute right-0 top-0 bottom-0 cursor-not-allowed"
                      icon="SparkleSimple"
                    />
                  )}
                </div>
              ))}
            </TwSelector>
          )}
        </Dialog.Content>
        <Dialog.Footer>
          <Button
            tw
            variant="primary"
            onClick={() => setIsSelectPropertyOpen(false)}
          >
            Klar
          </Button>
        </Dialog.Footer>
      </Dialog>
    </>
  );
}
